import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Heading,
  Input,
  Wrap,
  Separator,
} from '@chakra-ui/react';
import { Tags, Save, Search, X } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import { useAssignGroupsToConditionMutation, useGetConditionLookupQuery } from '../api';
import { useGetConditionGroupsLookupsQuery } from '@entities/condition-groups/api';
import ErrorAlert from '@/components/ErrorAlert';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import withHashDrawer from '@/components/ui/withHashDrawer';

interface ConditionGroupsModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of condition to fetch (for hash modal)
  onSuccess?: () => void;
}

const ConditionGroupsModal: React.FC<ConditionGroupsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Fetch condition data based on entityId
  const { data: condition } = useGetConditionLookupQuery(entityId!, {
    skip: !entityId,
  });
  
  const { 
    data: allGroupsResponse, 
    isLoading: isLoadingGroups,
    isError: isGroupsError,
    error: groupsError 
  } = useGetConditionGroupsLookupsQuery();
  const allGroups = allGroupsResponse?.data || [];
  
  const [
    assignGroups,
    { isLoading: isSaving, error: saveError }
  ] = useAssignGroupsToConditionMutation();

  // Handle close with cleanup
  const handleClose = () => {
    setSelectedGroupIds([]);
    setSearchTerm('');
    onClose();
  };

  // Initialize selected groups when modal opens
  useEffect(() => {
    if (condition && condition.group_ids) {
      setSelectedGroupIds(condition.group_ids);
    } else {
      setSelectedGroupIds([]);
    }
  }, [condition, isOpen]);

  const handleSave = async () => {
    if (!condition) return;

    try {
      await assignGroups({
        id: condition.id,
        data: { group_ids: selectedGroupIds }
      }).unwrap();
      
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to assign groups:', error);
    }
  };

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return allGroups;
    return allGroups.filter(group => 
      group.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allGroups, searchTerm]);

  // Get selected groups for display
  const selectedGroups = useMemo(() => {
    return allGroups.filter(group => selectedGroupIds.includes(group.id));
  }, [allGroups, selectedGroupIds]);

  // Get available (unselected) groups for selection
  const availableGroups = useMemo(() => {
    return filteredGroups.filter(group => !selectedGroupIds.includes(group.id));
  }, [filteredGroups, selectedGroupIds]);

  const hasChanges = useMemo(() => {
    if (!condition?.group_ids) return selectedGroupIds.length > 0;
    
    const currentGroupIds = [...condition.group_ids].sort();
    const newGroupIds = [...selectedGroupIds].sort();
    
    return JSON.stringify(currentGroupIds) !== JSON.stringify(newGroupIds);
  }, [condition, selectedGroupIds]);

  const isValid = selectedGroupIds.length > 0;
  const canSave = hasChanges && isValid;

  const handleRemoveGroup = (groupId: number) => {
    setSelectedGroupIds(prev => prev.filter(id => id !== groupId));
  };

  const handleAddGroup = (groupId: number) => {
    setSelectedGroupIds(prev => [...prev, groupId]);
  };

  if (!condition) return null;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg">
      <DrawerHeader>
        <DrawerTitle>
          <HStack>
            <Tags size={20} />
            <Text>Manage Condition Groups</Text>
          </HStack>
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        <VStack gap={4} align="stretch">
          {/* Condition Info */}
          <Box p={3} bg="gray.50" borderRadius="md" _dark={{ bg: "gray.700" }}>
            <HStack justify="space-between">
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                  Condition
                </Text>
                <Heading size="sm">{condition.name}</Heading>
              </VStack>
              <Badge 
                colorPalette={condition.status ? 'green' : 'gray'}
                size="sm"
              >
                {condition.status ? 'Active' : 'Inactive'}
              </Badge>
            </HStack>
          </Box>

          {/* Error Alert */}
          {(isGroupsError || saveError || (!isValid && hasChanges)) && (
            <ErrorAlert
              isError={true}
              title="Error"
              message={
                !isValid && hasChanges 
                  ? "At least one condition group must be selected"
                  : groupsError?.toString() || saveError?.toString() || "An error occurred"
              }
            />
          )}

          {isLoadingGroups ? (
            <LoadingSkeleton rows={3} />
          ) : (
            <VStack gap={4} align="stretch">
              {/* Selected Groups Display */}
              <VStack gap={3} align="stretch">
                <HStack justify="space-between">
                  <VStack align="start" gap={0}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700" _dark={{ color: "gray.300" }}>
                      Selected Groups ({selectedGroupIds.length})
                    </Text>
                    <Text fontSize="xs" color="gray.500" _dark={{ color: "gray.400" }}>
                      At least one group is required
                    </Text>
                  </VStack>
                  {selectedGroupIds.length > 1 && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setSelectedGroupIds([])}
                      colorPalette="red"
                    >
                      Clear All
                    </Button>
                  )}
                </HStack>
                
                {selectedGroups.length > 0 ? (
                  <Wrap gap={2}>
                    {selectedGroups.map((group) => (
                      <Badge
                        key={group.id}
                        colorPalette="blue"
                        size="md"
                        variant="subtle"
                        pr={1}
                      >
                        <HStack gap={1}>
                          <Text>{group.name}</Text>
                                                     <Button
                             size="xs"
                             variant="ghost"
                             p={0}
                             minH="auto"
                             h="auto"
                             onClick={() => handleRemoveGroup(group.id)}
                             disabled={selectedGroupIds.length === 1}
                             _hover={{ bg: "blue.200", _dark: { bg: "blue.700" } }}
                           >
                            <X size={12} />
                          </Button>
                        </HStack>
                      </Badge>
                    ))}
                  </Wrap>
                                 ) : (
                   <Box p={3} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200" _dark={{ bg: "red.900", borderColor: "red.700" }}>
                     <Text fontSize="sm" color="red.600" _dark={{ color: "red.300" }} textAlign="center">
                       No groups selected - at least one is required
                     </Text>
                   </Box>
                 )}
              </VStack>

              <Separator />

              {/* Search and Available Groups */}
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" _dark={{ color: "gray.300" }}>
                  Add Groups
                </Text>
                
                {/* Search Input */}
                <Box position="relative">
                  <Input
                    placeholder="Search groups..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="sm"
                    pl={10}
                  />
                  <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" pointerEvents="none">
                    <Search size={16} color="gray" />
                  </Box>
                </Box>

                {/* Available Groups */}
                {allGroups.length === 0 ? (
                  <Box p={4} textAlign="center" color="gray.500">
                    <Text>No condition groups available</Text>
                  </Box>
                ) : availableGroups.length === 0 ? (
                  <Box p={3} bg="gray.50" borderRadius="md" _dark={{ bg: "gray.800" }}>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      {searchTerm ? 'No groups match your search' : 'All groups are already selected'}
                    </Text>
                  </Box>
                ) : (
                  <VStack gap={2} align="stretch" maxH="200px" overflowY="auto">
                    {availableGroups.map((group) => (
                      <Box
                        key={group.id}
                        p={3}
                        borderWidth={1}
                        borderRadius="md"
                        borderColor="gray.200"
                        bg="white"
                        _dark={{
                          borderColor: "gray.600",
                          bg: "gray.700"
                        }}
                        cursor="pointer"
                        onClick={() => handleAddGroup(group.id)}
                        _hover={{
                          borderColor: "brand.300",
                          bg: "brand.50",
                          _dark: { 
                            borderColor: "brand.400",
                            bg: "brand.900"
                          }
                        }}
                      >
                        <VStack align="start" gap={1}>
                          <Text fontWeight="medium">{group.name}</Text>
                          <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>
                            ID: {group.id}
                            {group.order && ` • Order: ${group.order}`}
                          </Text>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </VStack>
            </VStack>
          )}
        </VStack>
      </DrawerBody>

      <DrawerFooter>
        <HStack gap={3} width="full">
          <Button
            variant="ghost"
            onClick={handleClose}
            flex={1}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSave}
            loading={isSaving}
            loadingText="Saving..."
            flex={1}
            disabled={!canSave || isLoadingGroups}
          >
            <HStack>
              <Save size={16} />
              <Text>Save Changes</Text>
            </HStack>
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Export wrapped version for hash modal (groups action)
export default withHashDrawer(ConditionGroupsModal, {
  action: 'groups' // Manage groups action: /#/123/groups
}); 